import React, { useState } from 'react';
import { User } from 'lucide-react';
import Button from '@components/common/Button';
import MultiRoleAssignmentModal from '@components/common/MultiRoleAssignmentModal';
import { formatPersonName } from '@utils/stringUtils';
import type { User as AppUser } from '@app-types';
import styles from './ContractFormModal.module.css';

interface ContractRolePickerProps {
  roleType: 'owner' | 'manager';
  selectedIds: number[];
  users: AppUser[];
  /**
   * The existing contract's own assignees. `users` is loaded separately and can lag behind or
   * lack someone (e.g. an inactive user), so these keep a known assignee shown by name.
   */
  currentAssignees?: Array<{ id: number; name: string }>;
  contractNumber: string;
  supplierName?: string;
  disabled?: boolean;
  onChange: (ids: number[]) => void;
}

const ROLE_LABEL: Record<'owner' | 'manager', string> = {
  owner: 'Contract Owner',
  manager: 'Contract Manager'
};

const ContractRolePicker: React.FC<ContractRolePickerProps> = ({
  roleType,
  selectedIds,
  users,
  currentAssignees = [],
  contractNumber,
  supplierName,
  disabled,
  onChange
}) => {
  const [showModal, setShowModal] = useState(false);
  const label = ROLE_LABEL[roleType];
  const nameOf = (id: number): string | undefined => {
    const user = users.find((candidate) => candidate.id === id);
    return user ? formatPersonName(user) : currentAssignees.find((assignee) => assignee.id === id)?.name;
  };
  const displayNames = selectedIds
    .map(nameOf)
    .filter((name): name is string => name !== undefined)
    .join(', ');
  const isAssigned = selectedIds.length > 0;

  return (
    <>
      <div className={styles.roleSelectionSection}>
        <span className={styles.formLabel}>{label}</span>
        {isAssigned ? (
          <div className={styles.selectedSupplierDisplay}>
            <div className={styles.supplierInfo}>
              <User size={16} />
              <div>
                <div className={styles.supplierName}>{displayNames || `${selectedIds.length} selected`}</div>
              </div>
            </div>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => setShowModal(true)}
              disabled={disabled}
              dataTestId={`contract-change-${roleType}-btn`}
            >
              Change
            </Button>
          </div>
        ) : (
          <Button
            type="button"
            variant="secondary"
            onClick={() => setShowModal(true)}
            disabled={disabled}
            dataTestId={`contract-select-${roleType}-btn`}
          >
            <User size={16} />
            Select {label}
          </Button>
        )}
      </div>

      {showModal && (
        <MultiRoleAssignmentModal
          contractNumber={contractNumber}
          supplierName={supplierName}
          roleType={roleType}
          currentAssigneeIds={selectedIds}
          onClose={() => setShowModal(false)}
          onAssigned={async (ids) => {
            onChange(ids);
          }}
          dataTestId={`contract-form-${roleType}`}
        />
      )}
    </>
  );
};

export default ContractRolePicker;
